import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Keeps product-page shipping threshold in sync with variant, quantity, and cart updates.
 *
 * @extends {Component}
 */
class ShippingThresholdProduct extends Component {
  /** @type {Record<string, number>} */
  #variantPrices = {};

  /** @type {number} */
  #thresholdCents = 0;

  /** @type {number} */
  #cartTotalCents = 0;

  /** @type {number} */
  #variantLineInCartCents = 0;

  /** @type {string} */
  #variantId = '';

  connectedCallback() {
    super.connectedCallback();
    this.#syncFromDataset();

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    closestSection?.addEventListener('change', this.#onQuantityChange);
    closestSection?.addEventListener('input', this.#onQuantityChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    closestSection?.removeEventListener('change', this.#onQuantityChange);
    closestSection?.removeEventListener('input', this.#onQuantityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #syncFromDataset() {
    this.#thresholdCents = Number(this.dataset.thresholdCents || 0);
    this.#cartTotalCents = Number(this.dataset.cartTotalCents || 0);
    this.#variantLineInCartCents = Number(this.dataset.variantLineInCart || 0);
    this.#variantId = String(this.dataset.initialVariantId || '');
    this.#variantPrices = this.#parseVariantPrices(this.dataset.variantPrices);
  }

  /**
   * @param {string | undefined} raw
   * @returns {Record<string, number>}
   */
  #parseVariantPrices(raw) {
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  #getQuantity() {
    const root = this.closest('.shopify-section, dialog') ?? document;
    const quantityInput = root.querySelector(
      'product-form-component input[name="quantity"], quantity-selector input[type="number"]'
    );
    const quantity = parseInt(quantityInput?.value ?? '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #getVariantPriceCents() {
    const fromMap = this.#variantPrices[this.#variantId];
    if (typeof fromMap === 'number') return fromMap;
    return Number(this.dataset.initialVariantPrice || 0);
  }

  #getEffectiveTotalCents() {
    const quantity = this.#getQuantity();
    return (
      this.#cartTotalCents -
      this.#variantLineInCartCents +
      this.#getVariantPriceCents() * quantity
    );
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const updated = event.detail.data.html.querySelector('shipping-threshold-product');
    if (updated instanceof HTMLElement) {
      this.dataset.cartTotalCents = updated.dataset.cartTotalCents ?? this.dataset.cartTotalCents;
      this.dataset.variantPrices = updated.dataset.variantPrices ?? this.dataset.variantPrices;
      this.dataset.initialVariantId = updated.dataset.initialVariantId ?? this.dataset.initialVariantId;
      this.dataset.initialVariantPrice =
        updated.dataset.initialVariantPrice ?? this.dataset.initialVariantPrice;
      this.dataset.variantLineInCart = updated.dataset.variantLineInCart ?? this.dataset.variantLineInCart;
      this.#syncFromDataset();
      morph(this, updated, { childrenOnly: true });
      return;
    }

    const variantId = event.detail.resource?.id;
    if (variantId) {
      this.#variantId = String(variantId);
      this.#render();
    }
  };

  /**
   * @param {import('@theme/events').CartUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    const totalPrice = event.detail.resource?.total_price;
    if (typeof totalPrice !== 'number') return;
    this.#cartTotalCents = totalPrice;
    this.dataset.cartTotalCents = String(totalPrice);
    this.#variantLineInCartCents = this.#findVariantLineInCart(event.detail.resource);
    this.dataset.variantLineInCart = String(this.#variantLineInCartCents);
    this.#render();
  };

  /**
   * @param {Event} event
   */
  #onQuantityChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target?.matches('input[name="quantity"]')) return;
    this.#render();
  };

  #render() {
    const panel = this.querySelector('.shipping-threshold');
    if (!panel || this.#thresholdCents <= 0) return;

    const effectiveTotal = this.#getEffectiveTotalCents();
    const qualifies = effectiveTotal >= this.#thresholdCents;
    const remainingCents = qualifies ? 0 : this.#thresholdCents - effectiveTotal;
    const progress = qualifies
      ? 100
      : Math.min(100, Math.round((effectiveTotal * 100) / this.#thresholdCents));

    panel.classList.toggle('shipping-threshold--unlocked', qualifies);

    const title = panel.querySelector('.shipping-threshold__title');
    const subtitle = panel.querySelector('.shipping-threshold__subtitle');
    const meter = panel.querySelector('.shipping-threshold__meter');
    const meterFill = panel.querySelector('.shipping-threshold__meter-fill');

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EUR';
    const rateMoney = formatMoney(this.#rateCents(), moneyFormat, currency);
    const thresholdMoney = formatMoney(this.#thresholdCents, moneyFormat, currency);
    const remainingMoney = formatMoney(remainingCents, moneyFormat, currency);

    if (title instanceof HTMLElement && subtitle instanceof HTMLElement) {
      if (qualifies) {
        title.textContent = this.dataset.textUnlocked || '';
        subtitle.textContent = this.dataset.textUnlockedHint || '';
      } else {
        title.textContent = (this.dataset.textRemaining || '').replace('[[remaining]]', remainingMoney);
        subtitle.textContent = (this.dataset.textRateWithFree || '')
          .replace('[[rate]]', rateMoney)
          .replace('[[threshold]]', thresholdMoney);
      }
    }

    if (meter instanceof HTMLElement) {
      meter.setAttribute('aria-valuenow', String(progress));
      meter.setAttribute(
        'aria-label',
        (this.dataset.textProgress || '').replace('[[percent]]', String(progress))
      );
    }

    if (meterFill instanceof HTMLElement) {
      meterFill.style.setProperty('--shipping-progress', `${progress}%`);
    }
  }

  #rateCents() {
    return Number(this.dataset.rateCents || 0);
  }

  /**
   * @param {{ items?: Array<{ variant_id: number, final_line_price?: number }> } | undefined} cart
   * @returns {number}
   */
  #findVariantLineInCart(cart) {
    if (!cart?.items?.length) return 0;
    const variantId = Number(this.#variantId);
    return cart.items.reduce((sum, item) => {
      if (Number(item.variant_id) !== variantId) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }
}

if (!customElements.get('shipping-threshold-product')) {
  customElements.define('shipping-threshold-product', ShippingThresholdProduct);
}
